/**
 * Cart deadline guard — 予約締切＋在庫のカート側再検証
 *
 * rental-calendar.js は「カートに入れる」時点でしか予約締切（開始日の
 * 2日前15時JST、以降は3日前扱い）と在庫を検証しない。Shopifyのカートは
 * 永続するため、追加時に有効だった明細は状況が変わっても有効なまま残り、
 * チェックアウト自体は何も再検証しない（#2440 で締切が、#2548 で在庫が
 * 夜間決済ですり抜けた）。このスクリプトがカート層でその隙間を塞ぐ:
 *
 *   1. カート描画時（カートページ / ドロワー / 追加通知）: 期限切れ明細が
 *      あればチェックアウトボタンを無効化し、ボタン直前に案内を表示する。
 *   2. チェックアウトボタン押下時（最終ゲート）: /cart.js を取り直して
 *      現在時刻で締切だけを再検証する（時刻計算のみ・API を呼ばない）。
 *      カート画面を開いたまま15時をまたいだ放置タブもここで捕まる。
 *   3. 在庫の再検証（NOB-1385）: availability API の validate モードで
 *      空きを確かめる。描画時だけ実行し、_validated_at（カート追加時の
 *      サーバ検証時刻）が VALIDATED_STALE_MS より古い明細がある時に限る。
 *      応答は STOCK_CHECK_TIMEOUT_MS で打ち切って fail-open。空きが
 *      なくなっていた場合はブロックし、テーマ設定の候補機種から
 *      「◯◯なら空きあり」の乗り換えリンクを提案する（NOB-1123 の
 *      日程引き継ぎ ?tr_start&tr_days に接続）。
 *      押下時には在庫 API を呼ばない（NOB-1766・2026-11-03 ノブヒロ判断）:
 *      以前は押下時にも必ず 1 回 validate していたが、/cart.js → validate →
 *      /cart.js の直列待ちで、毎回の客が決済画面の手前で 1〜2.5 秒止まって
 *      いた（実測: 日本の実機で約 1 秒、海外からは関数が東京の DB と往復して
 *      2 秒超）。それで守れる事故は「カートに入れてから押すまでの数分の間に
 *      他の予約で埋まった」場合だけで、それは ops 側の受注 webhook
 *      （在庫超過すり抜けアラート・overbook-realtime.ts）が事後に拾う。
 *      Shop Pay ボタンはもともと押下時に検証できず描画時だけで守っている
 *      ので、通常ボタンも同じ守り方に揃えた。押下時の API 呼び出しを
 *      戻さないこと。
 *
 * さらに「日程プロパティを持たないレンタル明細」もブロックする（#2444）。
 * 顧客アカウントの再購入ボタンや /cart/add 直リンクは rental-calendar.js を
 * 経由しないため、レンタル開始日・配達時間帯なしの素のバリアントがカートに
 * 入り、そのまま決済できてしまう。日程のないレンタル明細は期限切れと同様に
 * チェックアウトを塞ぎ、商品ページのカレンダーからの予約し直しを案内する。
 *
 * 締切の判定は時刻計算のみでAPI非依存（現在時刻と開始日だけで決まる）。
 * 在庫の判定は API 依存だが fail-open — 検証できない時（通信断・タイムアウト）
 * はチェックアウトを止めない（インフラ起因で売上を落とさない）。/checkout
 * 直リンクや端末時計の狂い等の残余経路と、描画時の検証〜決済完了の数分の
 * レース（押下時は在庫を見ない）は、TechRental-ops 側の webhook（受注時の
 * 締切すり抜け / 在庫超過すり抜けアラート）が検知する。
 *
 * API の URL には REGION_QUERY（forceFunctionRegion=ap-northeast-1）を付ける
 * （NOB-1766）。Supabase Edge Functions は呼び出し元の最寄りリージョンで動く
 * ため、海外・AI エージェント経由では関数が東京の DB と何往復もして 1.5〜3 秒
 * かかっていた。固定すると DB と同じ東京で動く。rental-calendar.js と
 * templates/agents.md.liquid（AI 向けの案内 URL）も同じ値を持つ。
 *
 * 既知の限界: 同一機種・同一日程の複数行は合算して検証するが、同一機種で
 * 「部分的に重なる別日程」の複数行は行ごとの検証のまま（合算需要を見ない）。
 * この形の注文は実質存在せず、ops 側アラートが事後に拾う。
 *
 * 設定は layout/theme.liquid が render する snippets/cart-guard-config.liquid の
 * JSON（#cart-rental-guard-config: apiUrl + altProducts）から読む。
 * API URL の正本はテーマ設定「レンタル予約 > Availability API URL」。
 *
 * NOTE: LEAD_TIME_DAYS_BASE / LATE_CUTOFF_HOUR_JST は
 * assets/rental-calendar.js および TechRental-ops の
 * _shared/booking-lead.ts と同期を保つこと。
 * canonicalModelKey は rental-calendar.js（および ops 側 availability の
 * resolveProductKey）と同じ正規化を保つこと。
 */
(function () {
  'use strict';

  var LEAD_TIME_DAYS_BASE = 2;
  var LATE_CUTOFF_HOUR_JST = 15;
  var NOTICE_CLASS = 'cart-deadline-guard-notice';
  var JST_OFFSET_MS = 9 * 60 * 60 * 1000;
  var WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

  // ── 在庫再検証（NOB-1385）の定数 ──
  // _validated_at がこれより古い明細があれば、描画時にも在庫を再検証する。
  var VALIDATED_STALE_MS = 30 * 60 * 1000;
  // 描画時チェックのスロットル: クリーン（問題なし）だった検証結果を
  // この期間は再利用する。ページ遷移をまたいで効かせるため sessionStorage に
  // 記録する（refreshNotices は全ページで走るので、メモリ内だけだと
  // 回遊中の各ページで API を叩いてしまう）。問題を検知した時は記録を消し、
  // 次の描画で必ず再検証する（解消をすぐ拾うため）。
  var STOCK_RECHECK_MS = 5 * 60 * 1000;
  var STOCK_STAMP_KEY = 'tr_cart_stock_checked_at';
  // availability v2 の契約: ペイロードに無い日付は REMAINING_CAP(4) 台以上の空き。
  // 候補機種の窓チェックはこの前提で「数値があり quantity 未満」の日だけ塞ぐ。
  var ALT_SHIP_LEAD_FALLBACK = 2;
  var ALT_RETURN_BUFFER_FALLBACK = 3;
  // 在庫 validate の打ち切り（NOB-1766）。超えたら fail-open（検証できなかった
  // 扱い＝塞がない）。API の p90 は日本から 0.65 秒・海外から 1.5〜2.5 秒
  // （2026-11-03 の edge logs）。伸ばしても Shop Pay ボタンが隠れる時間が
  // 伸びるだけで守りは増えない（押下時に API を待たない設計のため）。
  var STOCK_CHECK_TIMEOUT_MS = 1500;
  // availability API を DB と同じ東京リージョンで実行させる（NOB-1766・
  // ヘッダ参照）。クエリパラメータなので CORS のプリフライトは増えない。
  // rental-calendar.js の REGION_QUERY と同じ値を保つこと。
  var REGION_QUERY = '&forceFunctionRegion=ap-northeast-1';

  // 現在のJST壁時計を「UTCゲッターで読むDate」として返す。
  // getTimezoneOffset()でローカル→UTC変換してからローカルゲッターで読む方式は、
  // 閲覧環境がDSTを持つ場合に切替時刻付近で1時間ズレるため使わない
  // （TechRental-ops の _shared/date-utils.ts と同じパターン）。
  function nowJST(nowMs) {
    return new Date((nowMs || Date.now()) + JST_OFFSET_MS);
  }

  function earliestStartDate(nowMs) {
    var jst = nowJST(nowMs);
    var lead = jst.getUTCHours() >= LATE_CUTOFF_HOUR_JST
      ? LEAD_TIME_DAYS_BASE + 1
      : LEAD_TIME_DAYS_BASE;
    var d = new Date(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate());
    d.setDate(d.getDate() + lead);
    return d;
  }

  // ISO8601インスタント（JSTの壁時計を表す）→ ローカル深夜0時のDate。
  function jstDateFromISO(iso) {
    var t = new Date(iso);
    if (isNaN(t.getTime())) return null;
    var j = new Date(t.getTime() + JST_OFFSET_MS);
    return new Date(j.getUTCFullYear(), j.getUTCMonth(), j.getUTCDate());
  }

  // 表示用日付（YYYY.MM.DD 等）→ ローカル深夜0時のDate。
  function dateFromVisible(value) {
    var m = String(value).match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  }

  // 明細の開始日をローカル深夜0時の Date で返す。第一候補は隠しプロパティ
  // _start_iso8601（JST深夜0時のインスタント）、フォールバックは表示用の
  // 「レンタル開始日」(YYYY.MM.DD)。レンタル明細でなければ null。
  function lineStartDate(item) {
    var props = item.properties || {};
    if (props['_start_iso8601']) {
      var d = jstDateFromISO(props['_start_iso8601']);
      if (d) return d;
    }
    if (props['レンタル開始日']) return dateFromVisible(props['レンタル開始日']);
    return null;
  }

  // 明細の終了日（lineStartDate と対称）。_finish_iso8601 は終了日 23:59:59 JST
  // のインスタントなので、JST日付に落とすとそのまま終了日になる。
  function lineEndDate(item) {
    var props = item.properties || {};
    if (props['_finish_iso8601']) {
      var d = jstDateFromISO(props['_finish_iso8601']);
      if (d) return d;
    }
    if (props['レンタル終了日']) return dateFromVisible(props['レンタル終了日']);
    return null;
  }

  // レンタル本体の明細か（延長・調整明細を除く）。日程プロパティの有無に
  // 依存しない判定が必要なため、商品ハンドル/商品名で判定する。
  // 「レンタル延長（…）」「延長料金」等の調整系商品は決済リンク経由が正で、
  // 日程プロパティを持たないのが正常のため除外する。
  function isRentalMainLine(item) {
    var props = item.properties || {};
    if (props['_is_extension']) return false;
    var text = (item.handle || '') + ' ' + (item.product_title || item.title || '');
    if (text.indexOf('延長') !== -1) return false;
    return /レンタル|rental/i.test(text);
  }

  // カートの問題点を収集する（同期・時刻計算のみ）。
  //   expired: 予約締切を過ぎた開始日（重複除去済み。延長明細も同じ
  //            _start_iso8601 を持つため日付単位でまとめる）
  //   missing: 日程プロパティを持たないレンタル明細の商品名
  //            （再購入ボタン・直リンク等、カレンダー非経由の追加）
  function collectIssues(cart, nowMs) {
    var earliest = earliestStartDate(nowMs);
    var expired = [];
    var missing = [];
    var seenDate = {};
    var seenTitle = {};
    (cart.items || []).forEach(function (item) {
      var start = lineStartDate(item);
      if (start) {
        if (start < earliest && !seenDate[start.getTime()]) {
          seenDate[start.getTime()] = true;
          expired.push(start);
        }
      } else if (isRentalMainLine(item)) {
        var title = item.product_title || item.title || 'レンタル商品';
        if (!seenTitle[title]) {
          seenTitle[title] = true;
          missing.push(title);
        }
      }
    });
    return { expired: expired, missing: missing };
  }

  function hasIssues(issues) {
    return issues.expired.length > 0 || issues.missing.length > 0;
  }

  function formatMD(d) {
    return (d.getMonth() + 1) + '/' + d.getDate();
  }

  function formatMDW(d) {
    return formatMD(d) + '(' + WEEKDAY_LABELS[d.getDay()] + ')';
  }

  function formatDateYMD(d) {
    var m = String(d.getMonth() + 1);
    var day = String(d.getDate());
    return d.getFullYear() + '-' + (m.length === 1 ? '0' + m : m) + '-' +
      (day.length === 1 ? '0' + day : day);
  }

  function addDays(d, n) {
    var result = new Date(d);
    result.setDate(result.getDate() + n);
    return result;
  }

  function formatPrice(n) {
    return '¥' + n.toLocaleString('ja-JP');
  }

  function noticeText(issues) {
    var parts = [];
    if (issues.expired.length > 0) {
      var list = issues.expired.map(formatMD).join('、');
      parts.push('レンタル開始日 ' + list + ' は予約締切（開始日の2日前15時）を過ぎたため、ご注文いただけなくなりました。お手数ですが該当商品をカートから削除し、日付を選び直してください。');
    }
    if (issues.missing.length > 0) {
      parts.push('「' + issues.missing.join('」「') + '」はレンタル日程が選択されていません（再購入ボタンやリンクから追加された場合、日程情報が付きません）。お手数ですが該当商品をカートから削除し、商品ページの「日程を選んで予約する」から日程を選んでご予約ください。');
    }
    return parts.join('\n');
  }

  // 表示だけをまとめる。端末とAPIの両方が同じ開始日の締切超過を返しても、
  // 判定結果（stockIssues）やチェックアウトのブロックは変更しない。
  function visibleStockIssues(issues, stockList) {
    return (stockList || []).filter(function (issue) {
      return issue.reason !== 'too_soon' || !issues.expired.some(function (date) {
        return date.getTime() === issue.target.start.getTime();
      });
    });
  }

  // 通知から既存の削除操作へ移動するための明細。日程・在庫の判定には使わない。
  function recoveryLines(cart, issues, stockList) {
    var stockKeys = {};
    (stockList || []).forEach(function (issue) {
      var keys = issue.target && issue.target.keys ? issue.target.keys : [issue.key];
      keys.forEach(function (key) { stockKeys[key] = true; });
    });
    return (cart.items || []).map(function (item) {
      var start = lineStartDate(item);
      var expired = start && issues.expired.some(function (date) {
        return date.getTime() === start.getTime();
      });
      var missing = !start && isRentalMainLine(item);
      if (!expired && !missing && !stockKeys[item.key]) return null;
      return {
        key: item.key,
        label: (item.product_title || item.title || 'レンタル商品') +
          (start ? '（' + formatMD(start) + '開始）' : '')
      };
    }).filter(Boolean);
  }

  // ── 在庫再検証（NOB-1385）── ここから純粋ロジック ──

  // availability API の resolveProductKey / rental-calendar.js と同じ正規化。
  function canonicalModelKey(slug) {
    var m = String(slug).match(/galaxy-s(\d+)-ultra/i);
    return m ? 'galaxy-s' + m[1].toLowerCase() + '-ultra' : String(slug);
  }

  // 在庫検証の対象となるレンタル明細を抽出する。日程を持たない明細は
  // missing 側で既にブロックされるので対象外。
  //
  // 同一機種・同一日程の明細は1グループに合算する — _validated_at が
  // 追加ごとに異なるため、同じ商品・同じ日程を2回追加すると Shopify は
  // 数量2に統合せず quantity:1 の別行として保持する。行ごとに quantity=1 で
  // 検証すると残り1台でも両方が available:true になり、合計2台の需要が
  // 通ってしまう（Codex P1指摘）。需要は (機種, 開始, 終了) 単位の
  // 合算数量で検証する。合算数量が1でないグループは isValidationStale が
  // 常に要再検証扱いにする。
  function collectStockTargets(cart) {
    var groups = {};
    var order = [];
    (cart.items || []).forEach(function (item) {
      if (!isRentalMainLine(item)) return;
      var start = lineStartDate(item);
      var end = lineEndDate(item);
      if (!start || !end || end < start) return;
      var props = item.properties || {};
      var extDays = parseInt(props['_extension_days'], 10);
      if (isNaN(extDays)) extDays = 0;
      var validatedAt = props['_validated_at'] ? Date.parse(props['_validated_at']) : NaN;
      // _validated_at 無し（旧カート・カレンダー非経由）は「古い」扱い＝安全側
      var validatedAtMs = isFinite(validatedAt) ? validatedAt : null;
      var price = typeof item.final_price === 'number' ? item.final_price
        : (typeof item.price === 'number' ? item.price : null);
      var lineKey = item.key || String(item.id);
      var groupKey = canonicalModelKey(item.handle || '') + '|' +
        formatDateYMD(start) + '|' + formatDateYMD(end);
      var g = groups[groupKey];
      if (!g) {
        g = {
          key: groupKey,
          keys: [lineKey],
          handle: item.handle || '',
          title: item.product_title || item.title || 'レンタル商品',
          quantity: item.quantity || 1,
          start: start,
          end: end,
          extensionDays: extDays,
          validatedAtMs: validatedAtMs,
          // Shopify cart.js の金額は最小単位×100（JPYでも100倍）
          unitPrice: price !== null ? price / 100 : null
        };
        groups[groupKey] = g;
        order.push(g);
      } else {
        g.keys.push(lineKey);
        g.quantity += item.quantity || 1;
        // 合算グループの鮮度・延長は安全側（最も古い/無い・最大）に倒す
        if (g.validatedAtMs === null || validatedAtMs === null) {
          g.validatedAtMs = null;
        } else {
          g.validatedAtMs = Math.min(g.validatedAtMs, validatedAtMs);
        }
        if (extDays > g.extensionDays) g.extensionDays = extDays;
        if (g.unitPrice === null && price !== null) g.unitPrice = price / 100;
      }
    });
    return order;
  }

  function isValidationStale(target, nowMs) {
    // カート追加時のサーバ検証は数量1で行われる（rental-calendar.js は
    // 1回の追加で quantity:1 しか入れない）。Shopifyは数量変更で明細
    // プロパティを保持するため、カート上で数量を増やした明細は
    // _validated_at が新しくても増加分を何も担保していない — タイムスタンプ
    // だけ見て新鮮扱いすると、Shop Pay（iframe・押下時ゲートで捕捉不可）が
    // 未検証の数量のまま開放される（Codex P1指摘）。quantity !== 1 は常に
    // 要再検証として扱う。
    if (target.quantity !== 1) return true;
    return target.validatedAtMs === null ||
      nowMs - target.validatedAtMs > VALIDATED_STALE_MS;
  }

  function hasStaleValidation(targets, nowMs) {
    for (var i = 0; i < targets.length; i++) {
      if (isValidationStale(targets[i], nowMs)) return true;
    }
    return false;
  }

  // クリーン結果スタンプの照合キー。何を検証したか（明細・日程・数量）を
  // 符号化し、カート内容が変わったスタンプを無効化する — 時刻だけの
  // スタンプだと、数量変更後もスロットル期間中は再検証がスキップされて
  // しまう（Codex P1指摘の後半）。順序に依存しないようソートして連結。
  function stockCheckSignature(targets) {
    return targets
      .map(function (t) {
        return t.key + '|' + formatDateYMD(t.start) + '|' +
          formatDateYMD(t.end) + '|' + t.quantity;
      })
      .sort()
      .join(',');
  }

  // validate モードURL。quantity はカート上で数量変更されたケースを拾う
  // （数量変更はカート層に再検証が無い唯一の入力でもある）。
  function buildValidateUrl(apiUrl, target) {
    return apiUrl +
      '?product=' + encodeURIComponent(target.handle) +
      '&validate_start=' + encodeURIComponent(formatDateYMD(target.start)) +
      '&validate_end=' + encodeURIComponent(formatDateYMD(target.end)) +
      '&quantity=' + encodeURIComponent(String(target.quantity)) +
      '&format=v2' + REGION_QUERY;
  }

  // validate レスポンス → 問題 or null。available が無い/true は通す
  // （fail-open: 旧APIや形の崩れたレスポンスで売上を止めない）。
  function stockIssueFromVerdict(target, data) {
    if (!data || data.available !== false) return null;
    return { target: target, reason: data.reason || 'full' };
  }

  // 複数商品モード（?products=）のレスポンス → slugごとの空きデータ。
  // rental-calendar.js fetchAltAvailability と同じ読み方。
  function parseAltAvailability(data, slugs) {
    var bySlug = {};
    if (!data || !data.results) return bySlug;
    for (var s = 0; s < slugs.length; s++) {
      var r = data.results[slugs[s]];
      if (!r || r.error || !r.dates || !Array.isArray(r.dates)) continue;
      var avail = {};
      for (var j = 0; j < r.dates.length; j++) {
        var entry = r.dates[j];
        if (entry && typeof entry.date === 'string' &&
            typeof entry.remaining === 'number' && isFinite(entry.remaining)) {
          avail[entry.date] = entry.remaining;
        }
      }
      bySlug[slugs[s]] = {
        availability: avail,
        shipLeadDays: typeof r.ship_lead_days === 'number' ? r.ship_lead_days : ALT_SHIP_LEAD_FALLBACK,
        returnBufferDays: typeof r.return_buffer_days === 'number' ? r.return_buffer_days : ALT_RETURN_BUFFER_FALLBACK,
        minDate: typeof r.min_date === 'string' ? r.min_date : null,
        maxDate: typeof r.max_date === 'string' ? r.max_date : null
      };
    }
    return bySlug;
  }

  function targetTotalDays(target) {
    return Math.round((target.end - target.start) / 86400000) + 1;
  }

  // 候補機種で同じ日程（物理窓全体）が quantity 台ぶん取れるか。
  // データが無い機種は false（「取れるかもしれない」で送客しない —
  // 遷移先で空きなしは最悪の体験・NOB-1123 と同方針）。
  function altRangeFree(ad, target) {
    if (!ad) return false;
    var startStr = formatDateYMD(target.start);
    if (ad.minDate && startStr < ad.minDate) return false;
    if (ad.maxDate && startStr > ad.maxDate) return false;
    var totalDays = targetTotalDays(target);
    for (var i = -ad.shipLeadDays; i < totalDays + ad.returnBufferDays; i++) {
      var rem = ad.availability[formatDateYMD(addDays(target.start, i))];
      if (typeof rem === 'number' && rem < target.quantity) return false;
    }
    return true;
  }

  // 「◯◯なら空きあり」チップを組み立てる。最大2件・価格が近い順
  // （NOB-1123 の却下バナーと同じ並べ方）。
  // 延長付き明細には出さない — 日程引き継ぎ（?tr_start&tr_days）は基本プラン
  // しか運べず、延長込みの希望を黙って短縮し価格も過小表示することになる
  // （NOB-1123 で延長選択中にチップを出さないのと同じ理由）。
  function buildAltChips(target, altProducts, altData) {
    if (target.extensionDays > 0) return [];
    var chips = [];
    var selfKey = canonicalModelKey(target.handle);
    var baseDays = targetTotalDays(target);
    for (var i = 0; i < (altProducts || []).length; i++) {
      var alt = altProducts[i];
      if (!alt || !alt.slug || !alt.url || !alt.label) continue;
      // 自機種は候補に出さない（handle→canonical key で比較）
      if (canonicalModelKey(alt.slug) === selfKey) continue;
      if (!altRangeFree(altData[alt.slug], target)) continue;
      var price = alt.prices ? alt.prices[String(baseDays)] : undefined;
      chips.push({
        slug: alt.slug,
        title: alt.title || alt.label,
        label: alt.label + 'なら空きあり' +
          (typeof price === 'number' ? ' ' + formatPrice(price) : ''),
        href: alt.url + (alt.url.indexOf('?') === -1 ? '?' : '&') +
          'tr_start=' + formatDateYMD(target.start) + '&tr_days=' + baseDays,
        priceDelta: (typeof price === 'number' && target.unitPrice !== null)
          ? Math.abs(price - target.unitPrice)
          : Infinity
      });
    }
    chips.sort(function (a, b) { return a.priceDelta - b.priceDelta; });
    return chips.slice(0, 2);
  }

  // 在庫問題の顧客向け文言。「満室」はホテル語なので使わず「空き」の語彙で
  // 統一する（カレンダー却下バナーと同じ・2026-08-08 ノブヒロ決定）。
  function stockIssueMessage(issue) {
    var t = issue.target;
    var range = formatMDW(t.start) + '〜' + formatMDW(t.end);
    if (issue.reason === 'too_soon') {
      // クライアント時計が狂っていて同期チェックを抜けたケース。締切文言に合わせる。
      return '「' + t.title + '」のレンタル開始日 ' + formatMD(t.start) +
        ' は予約締切（開始日の2日前15時）を過ぎたため、ご注文いただけなくなりました。お手数ですが該当商品をカートから削除し、日付を選び直してください。';
    }
    if (issue.reason === 'full') {
      return '「' + t.title + '」の ' + range +
        ' は、カートに入れている間に空きがなくなりました。お手数ですが該当商品をカートから削除し、別の日程をお選びください。';
    }
    // out_of_window 等（実質発生しない残余）
    return '「' + t.title + '」の ' + range +
      ' は現在ご予約いただけません。お手数ですが該当商品をカートから削除し、日付を選び直してください。';
  }

  // ── Node(テスト)用エクスポート ──
  // ブラウザでは document 必須の初期化に進む。Node には document が無いので、
  // 純粋ロジックだけ公開してここで終える（tests/cart-deadline-guard.test.js）。
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = {
        VALIDATED_STALE_MS: VALIDATED_STALE_MS,
        earliestStartDate: earliestStartDate,
        lineStartDate: lineStartDate,
        lineEndDate: lineEndDate,
        isRentalMainLine: isRentalMainLine,
        collectIssues: collectIssues,
        noticeText: noticeText,
        visibleStockIssues: visibleStockIssues,
        recoveryLines: recoveryLines,
        canonicalModelKey: canonicalModelKey,
        collectStockTargets: collectStockTargets,
        isValidationStale: isValidationStale,
        hasStaleValidation: hasStaleValidation,
        stockCheckSignature: stockCheckSignature,
        buildValidateUrl: buildValidateUrl,
        stockIssueFromVerdict: stockIssueFromVerdict,
        parseAltAvailability: parseAltAvailability,
        altRangeFree: altRangeFree,
        buildAltChips: buildAltChips,
        stockIssueMessage: stockIssueMessage
      };
    }
    return;
  }

  // ── ここから DOM / ネットワーク ──

  // 設定（snippets/cart-guard-config.liquid が head に出力するJSON）。
  // 無い・壊れている場合は在庫チェックを丸ごとスキップ（fail-open）。
  function readGuardConfig() {
    var el = document.getElementById('cart-rental-guard-config');
    if (!el) return { apiUrl: '', altProducts: [] };
    try {
      var c = JSON.parse(el.textContent);
      return {
        apiUrl: typeof c.apiUrl === 'string' ? c.apiUrl : '',
        altProducts: Array.isArray(c.altProducts) ? c.altProducts : []
      };
    } catch (e) {
      return { apiUrl: '', altProducts: [] };
    }
  }
  var guardConfig = readGuardConfig();

  // 最後の在庫検証結果。[{key, reason, target, message, chips}]
  var stockIssues = [];
  // 検証の世代番号。遅れて完了した古い検証が stockIssues と表示を上書き
  // しないよう、共有状態のコミットは最新世代だけに許す（レビュー指摘・
  // CodeRabbit）。もとは押下時ゲートが描画時チェックを追い越すために必要
  // だった。NOB-1766 で押下時の検証を撤去し、描画時チェック同士は走行中に
  // 開始しない（やり直しを予約する）ので実質 1 世代ずつだが、保険として残す。
  var stockCheckGeneration = 0;
  // 実行中の検証の世代（0=なし）。boolean ではなく世代を持つのは、
  // 追い越された古い検証の完了がフラグを下ろし損ねて描画時チェックが
  // 永久停止する形の詰まりを避けるため（自分が立てたフラグだけ下ろす）。
  var stockCheckInFlightGen = 0;
  // 検証走行中に描画イベント（cartUpdate等）が来た場合のやり直し予約。
  // 走行中の検証は変更前のカートを見ている可能性があるため、その完了を
  // 待ってからもう一度 refreshNotices を回す（Codex P1指摘: 走行中を
  // 理由に判定をスキップして復元すると、数量変更直後の Shop Pay が
  // 未検証のまま開放される）。
  var stockRecheckQueued = false;

  // 開きっぱなしのカートタブ対策（Codex P1指摘）: 全明細が新鮮で検証を
  // 見送った場合も、鮮度が切れる時刻（_validated_at + 30分）または
  // スロットルが明ける時刻に自前で再検証を予約する。既存のタイマーは
  // 15時JST/深夜の境界しか見ず、visibilitychange はタブを離れないと
  // 発火しないため、前面に出しっぱなしのタブでは Shop Pay（iframe・
  // 押下時ゲートで捕捉不可）が古い検証のまま出続けてしまう。
  var staleRefreshTimer = null;

  function scheduleStaleRefresh(targets, nowMs) {
    if (staleRefreshTimer !== null) {
      clearTimeout(staleRefreshTimer);
      staleRefreshTimer = null;
    }
    if (!guardConfig.apiUrl || targets.length === 0) return;
    var next = Infinity;
    for (var i = 0; i < targets.length; i++) {
      if (!isValidationStale(targets[i], nowMs)) {
        next = Math.min(next, targets[i].validatedAtMs + VALIDATED_STALE_MS);
      }
    }
    // 古い明細が残っている（＝スロットルで検証を見送った）場合は、
    // スロットルが明けたら再検証する。5分おきの緩いポーリングになるが、
    // 対象はカートUIを開いたままの前面タブだけで、Shop Pay を古い検証の
    // まま出し続けないための下限保証。
    var stamp = readStockStamp();
    if (stamp !== null && hasStaleValidation(targets, nowMs)) {
      next = Math.min(next, stamp.at + STOCK_RECHECK_MS);
    }
    // 在庫問題でブロック中も同じ間隔で再検証を予約する。ブロック中は
    // チェックアウトボタンが無効＝押下時ゲートが呼べないため、前面に
    // 出しっぱなしのタブでは空きが復活しても解除の契機が無い
    // （Codex P2指摘）。カート操作・タブ復帰があれば即時に再検証される。
    if (stockIssues.length > 0) {
      next = Math.min(next, nowMs + STOCK_RECHECK_MS);
    }
    if (!isFinite(next)) return;
    // 時計の誤差マージン5秒。最長でも30分後（VALIDATED_STALE_MS）。
    staleRefreshTimer = setTimeout(refreshNotices, Math.max(next - nowMs, 0) + 5000);
  }

  // スタンプは {at: 検証時刻ms, sig: stockCheckSignature} — 時刻と
  // 「何を検証したか」の両方を持つ。sig が現在のカートと一致しない
  // スタンプは無効（数量変更・明細追加削除で即座に再検証させる）。
  function readStockStamp() {
    try {
      var v = sessionStorage.getItem(STOCK_STAMP_KEY);
      if (!v) return null;
      var parsed = JSON.parse(v);
      if (!parsed || typeof parsed.at !== 'number' || typeof parsed.sig !== 'string') {
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeStockStamp(stamp) {
    try {
      if (stamp === null) sessionStorage.removeItem(STOCK_STAMP_KEY);
      else sessionStorage.setItem(STOCK_STAMP_KEY, JSON.stringify(stamp));
    } catch (e) {
      /* sessionStorage不可の環境ではスロットルなし（チェックが増える側に倒す） */
    }
  }

  // カートから消えた明細の在庫問題を落とす（削除で即座にブロック解除する）。
  // グループ（合算対象）の一部だけ残っている場合は保持する — 残った分は
  // stockIssues>0 が次の描画で必ず再検証させるので、新しい数量で判定し直る。
  function pruneStockIssues(cart) {
    var keys = {};
    (cart.items || []).forEach(function (item) {
      if (item.key) keys[item.key] = true;
    });
    stockIssues = stockIssues.filter(function (issue) {
      var lineKeys = issue.target && issue.target.keys ? issue.target.keys : [issue.key];
      for (var i = 0; i < lineKeys.length; i++) {
        if (keys[lineKeys[i]]) return true;
      }
      return false;
    });
  }

  // 描画時に在庫チェックを走らせるか。検証走行中の扱いは refreshNotices 側で
  // 判断する（ここで握ると「走行中＝スキップ＝復元」となり、走行中の検証が
  // 見ていない変更後のカートで Shop Pay が開放される）。
  function shouldRunStockCheck(targets, nowMs) {
    if (!guardConfig.apiUrl || targets.length === 0) return false;
    // 既知の問題がある間は毎回再検証（解消・明細変更をすぐ反映する）
    if (stockIssues.length > 0) return true;
    if (!hasStaleValidation(targets, nowMs)) return false;
    var stamp = readStockStamp();
    if (
      stamp !== null &&
      nowMs - stamp.at < STOCK_RECHECK_MS &&
      stamp.sig === stockCheckSignature(targets)
    ) {
      return false;
    }
    return true;
  }

  /**
   * 在庫を再検証する。forceAll=true（押下時ゲート）は全明細、false（描画時）
   * は「古い明細＋既に問題を検知済みの明細」だけを検証する。
   * fail-open: 個々の検証の通信・パース失敗はその明細を「問題なし」扱い。
   * done(list, isCurrent) — list は今回の検証結果。isCurrent=false は
   * この検証より新しい検証が始まっていた（結果は共有状態に反映していない）
   * ことを示し、呼び出し側は表示の上書きを控える。
   */
  // タイムアウト付きで JSON を取る（NOB-1766）。打ち切りはヘッダ受信では
  // なく本文を読み終えるまで効かせる（ヘッダだけ返して本文が止まる応答で
  // 1.5 秒の fail-open が効かず、Shop Pay が隠れたまま検証が終わらない・
  // Codex レビュー P2）。AbortController の無い環境では打ち切りなし
  // （従来どおり）。打ち切り・通信失敗・非 2xx は reject になり、呼び出し側の
  // catch が fail-open にする。
  function fetchJsonWithTimeout(url, timeoutMs) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller === null
      ? null
      : setTimeout(function () { controller.abort(); }, timeoutMs);
    var done = function () { if (timer !== null) clearTimeout(timer); };
    return (controller === null ? fetch(url) : fetch(url, { signal: controller.signal }))
      .then(function (res) {
        if (!res.ok) throw new Error('API error: ' + res.status);
        return res.json();
      })
      .then(
        function (data) { done(); return data; },
        function (err) { done(); throw err; }
      );
  }

  function runStockCheck(targets, forceAll, done) {
    var generation = ++stockCheckGeneration;
    var nowMs = Date.now();
    var flaggedKeys = {};
    stockIssues.forEach(function (issue) { flaggedKeys[issue.key] = true; });
    var toValidate = targets.filter(function (t) {
      return forceAll || flaggedKeys[t.key] || isValidationStale(t, nowMs);
    });
    if (toValidate.length === 0 || !guardConfig.apiUrl) {
      stockIssues = [];
      done(stockIssues, true);
      return;
    }


    stockCheckInFlightGen = generation;
    // done は1回だけ呼ぶ。finish() の中（applyNotices 等の呼び出し先を含む）で
    // 例外が出ると下の .catch が拾って done を二度目に呼んでしまい、
    // 「ブロック表示中に空リストで再入 → チェックアウトへ遷移」になり得る。
    var settled = false;
    var settle = function (list, isCurrent) {
      if (settled) return;
      settled = true;
      done(list, isCurrent);
    };
    var finish = function (issues, altData) {
      var computed = issues.map(function (issue) {
        return {
          key: issue.target.key,
          reason: issue.reason,
          target: issue.target,
          message: stockIssueMessage(issue),
          chips: issue.reason === 'full'
            ? buildAltChips(issue.target, guardConfig.altProducts, altData)
            : []
        };
      });
      if (stockCheckInFlightGen === generation) stockCheckInFlightGen = 0;
      var isCurrent = generation === stockCheckGeneration;
      if (isCurrent) {
        stockIssues = computed;
        // クリーンな結果だけスロットルに記録（検証したカート内容のsig付き —
        // 数量変更等でカートが変わればスタンプは効かない）。問題ありは記録を
        // 消して次の描画で必ず再検証させる（解消の取りこぼし防止）。
        writeStockStamp(
          issues.length === 0
            ? { at: Date.now(), sig: stockCheckSignature(targets) }
            : null,
        );
      }
      settle(computed, isCurrent);
      drainQueuedRecheck();
    };

    Promise.all(toValidate.map(function (target) {
      return fetchJsonWithTimeout(buildValidateUrl(guardConfig.apiUrl, target), STOCK_CHECK_TIMEOUT_MS)
        .then(function (data) { return stockIssueFromVerdict(target, data); })
        .catch(function () { return null; }); // fail-open（明細単位）
    })).then(function (results) {
      var issues = results.filter(Boolean);
      // 候補機種の空きは、full の問題があり候補設定が生きている時だけ取得
      var needAlt = guardConfig.altProducts.length > 0 &&
        issues.some(function (i) {
          return i.reason === 'full' && i.target.extensionDays === 0;
        });
      if (!needAlt) {
        finish(issues, {});
        return;
      }
      var slugs = guardConfig.altProducts
        .map(function (a) { return a && a.slug; })
        .filter(Boolean);
      fetchJsonWithTimeout(
        guardConfig.apiUrl + '?products=' + encodeURIComponent(slugs.join(',')) + '&format=v2' + REGION_QUERY,
        STOCK_CHECK_TIMEOUT_MS
      )
        .then(function (data) { finish(issues, parseAltAvailability(data, slugs)); })
        .catch(function () { finish(issues, {}); }); // 提案なしでブロックは維持
    }).catch(function () {
      // ここには来ない想定（明細単位でcatch済み）だが、万一の取りこぼしも
      // fail-open でチェックアウトを止めない。既に settle 済み（＝結果は
      // 出ていて、その後の描画で例外が出ただけ）なら判定は覆さない。
      if (settled) {
        drainQueuedRecheck();
        return;
      }
      if (stockCheckInFlightGen === generation) stockCheckInFlightGen = 0;
      var isCurrent = generation === stockCheckGeneration;
      if (isCurrent) stockIssues = [];
      settle([], isCurrent);
      drainQueuedRecheck();
    });
  }

  // 検証走行中に積まれた描画やり直しを、走行が終わったタイミングで消化する。
  // 走行中でなくなっていること（新しい検証が始まっていないこと）を確認して
  // から refreshNotices を回す — 新しい検証が既に走っているなら、その完了時の
  // drain がまた呼ばれるのでここでは何もしない。
  function drainQueuedRecheck() {
    if (!stockRecheckQueued || stockCheckInFlightGen !== 0) return;
    stockRecheckQueued = false;
    setTimeout(refreshNotices, 0);
  }

  function checkoutButtons() {
    return document.querySelectorAll('button[name="checkout"]');
  }

  // alert() 用の素のテキスト（チップは通知ブロック側で表示する）。
  function allNoticeText(issues, stockList) {
    var parts = [];
    var deadline = noticeText(issues);
    if (deadline) parts.push(deadline);
    visibleStockIssues(issues, stockList).forEach(function (issue) { parts.push(issue.message); });
    return parts.join('\n');
  }

  function findRemovalLink(key) {
    var rows = document.querySelectorAll('[data-cart-item-key]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].dataset.cartItemKey !== key) continue;
      var links = rows[i].querySelectorAll('cart-remove-button a, cart-remove-button button');
      for (var j = 0; j < links.length; j++) {
        if (links[j].getClientRects().length > 0 &&
            window.getComputedStyle(links[j]).visibility === 'visible') return links[j];
      }
    }
    return null;
  }

  // 削除は既存の Dawn の操作に任せる。ここでは移動とフォーカスだけを行い、
  // カート変更の通信・延長行の整合・ローディング制御を増やさない。
  function appendRecoveryLinks(notice, cart, issues, stockList) {
    var lines = recoveryLines(cart, issues, stockList);
    if (lines.length === 0) return;
    var list = document.createElement('ul');
    list.className = NOTICE_CLASS + '__recovery';
    lines.forEach(function (line) {
      var li = document.createElement('li');
      var link = document.createElement('a');
      var cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
      link.href = cartUrl;
      link.textContent = line.label + 'の削除へ';
      link.addEventListener('click', function (ev) {
        // 再描画後の要素をkeyで引き直す。行番号のずれで別商品へ誘導しない。
        var removal = findRemovalLink(line.key);
        if (!removal) return; // 追加通知など明細の無い面ではカートへ移動する
        ev.preventDefault();
        removal.scrollIntoView({ block: 'center' });
        removal.focus({ preventScroll: true });
      });
      li.appendChild(link);
      list.appendChild(li);
    });
    notice.appendChild(list);
  }

  // 締切/日程なしと在庫の案内をまとめ、該当明細の既存の削除操作へ誘導する。
  function buildNoticeElement(issues, stockList, cart) {
    var notice = document.createElement('div');
    notice.className = NOTICE_CLASS;
    notice.setAttribute('role', 'alert');

    var deadline = noticeText(issues);
    if (deadline) {
      var deadlineEl = document.createElement('div');
      deadlineEl.style.whiteSpace = 'pre-line';
      deadlineEl.textContent = deadline;
      notice.appendChild(deadlineEl);
    }

    visibleStockIssues(issues, stockList).forEach(function (issue) {
      var block = document.createElement('div');
      if (notice.childNodes.length > 0) block.style.marginTop = '0.8rem';
      var msg = document.createElement('div');
      msg.textContent = issue.message;
      block.appendChild(msg);
      if (issue.chips.length > 0) {
        var chipRow = document.createElement('div');
        issue.chips.forEach(function (chip) {
          var a = document.createElement('a');
          a.href = chip.href;
          a.textContent = chip.label;
          a.style.cssText =
            'display:inline-block;margin:0.6rem 0.8rem 0 0;padding:0.4rem 1rem;border:0.1rem solid currentColor;border-radius:2rem;color:inherit;font-weight:600;text-decoration:none;';
          a.addEventListener('click', function () {
            // 遷移はhrefに任せ、ここでは計測だけ（NOB-1123の却下チップと同じ語彙）
            if (window.TRAnalytics && TRAnalytics.alternativeClicked) {
              TRAnalytics.alternativeClicked(issue.target.title, chip.title, 'cart_guard_chip');
            }
          });
          chipRow.appendChild(a);
        });
        block.appendChild(chipRow);
      }
      notice.appendChild(block);
    });

    appendRecoveryLinks(notice, cart, issues, stockList);

    return notice;
  }

  // ── 描画時の案内表示 ──
  var noticeSequence = 0;

  function applyNotices(issues, stockList, cart) {
    var blocked = hasIssues(issues) || (stockList && stockList.length > 0);
    var existing = document.querySelectorAll('.' + NOTICE_CLASS);
    Array.prototype.forEach.call(existing, function (el) {
      el.parentNode.removeChild(el);
    });

    Array.prototype.forEach.call(checkoutButtons(), function (btn) {
      var describedBy = (btn.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
        return id && id !== btn.dataset.deadlineNoticeId;
      });
      delete btn.dataset.deadlineNoticeId;
      if (blocked) {
        btn.disabled = true;
        btn.dataset.deadlineBlocked = 'true';
        var notice = buildNoticeElement(issues, stockList, cart);
        notice.id = NOTICE_CLASS + '-' + (++noticeSequence);
        btn.dataset.deadlineNoticeId = notice.id;
        describedBy.push(notice.id);
        btn.parentNode.insertBefore(notice, btn);
      } else if (btn.dataset.deadlineBlocked) {
        delete btn.dataset.deadlineBlocked;
        // cart-extension-sync.js（延長行と本体の整合）が同じボタンを塞いでいる間は
        // 有効化しない。あちらも戻す時にこちらの印を見る（相互に印を尊重する）。
        if (!btn.dataset.extensionSyncGate) btn.disabled = false;
      }
      if (describedBy.length > 0) btn.setAttribute('aria-describedby', describedBy.join(' '));
      else btn.removeAttribute('aria-describedby');
    });

    setDynamicCheckoutHidden(blocked);
  }

  // Shop Pay 等の動的チェックアウトボタン（additional_checkout_buttons、
  // カートページの main-cart-footer が描画）は iframe 内のためクリックを
  // 横取りできない唯一の経路。ブロック中はコンテナごと非表示にして塞ぐ。
  function setDynamicCheckoutHidden(hidden) {
    var containers = document.querySelectorAll('.additional-checkout-buttons');
    Array.prototype.forEach.call(containers, function (el) {
      if (hidden) {
        el.style.display = 'none';
        el.dataset.deadlineBlocked = 'true';
      } else if (el.dataset.deadlineBlocked) {
        el.style.display = '';
        delete el.dataset.deadlineBlocked;
      }
    });
  }

  function refreshNotices() {
    if (checkoutButtons().length === 0) return;
    // 動的チェックアウトボタンは押下時に検証できないため、検証が
    // 返るまで悲観的に隠す（遅いレスポンス中の早押しすり抜け防止）。
    // 検証が通れば applyNotices が復元する。
    setDynamicCheckoutHidden(true);
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var issues = collectIssues(cart);
        pruneStockIssues(cart);
        var targets = collectStockTargets(cart);
        if (stockCheckInFlightGen !== 0) {
          // 検証走行中: 走行中の検証は「今の」カートを見ていない可能性が
          // ある（例: 数量変更直後の cartUpdate）ので、ここで判定を確定
          // させない。既知の問題があれば出しつつ、クリーン表示への復元は
          // しない＝エントリ時に隠した動的チェックアウトは隠れたまま。
          // 完了後に描画からやり直す（Codex P1指摘）。
          if (hasIssues(issues) || stockIssues.length > 0) {
            applyNotices(issues, stockIssues, cart);
          }
          stockRecheckQueued = true;
          return;
        }
        if (!shouldRunStockCheck(targets, Date.now())) {
          applyNotices(issues, stockIssues, cart);
          scheduleStaleRefresh(targets, Date.now());
          return;
        }
        // 在庫検証中: 既知の問題があれば先に出しておく。クリーンな場合は
        // 動的ボタンを隠したまま検証完了を待つ。通常のチェックアウト
        // ボタンは塞がない（検証は STOCK_CHECK_TIMEOUT_MS 以内に返り、
        // その間に押された分は ops の受注 webhook が拾う・NOB-1766）。
        if (hasIssues(issues) || stockIssues.length > 0) {
          applyNotices(issues, stockIssues, cart);
        }
        runStockCheck(targets, false, function (stockList, isCurrent) {
          // 後から始まった検証に追い越された古い検証は表示を上書きしない。
          // やり直しが予約されている（＝検証中にカートが変わった）場合も
          // クリーン描画しない — done → drain の順で呼ばれるため、ここで
          // 復元すると直後のやり直しまでの一瞬 Shop Pay が開く（Codex P1）。
          if (isCurrent && !stockRecheckQueued) {
            applyNotices(issues, stockList, cart);
            scheduleStaleRefresh(targets, Date.now());
          }
        });
      })
      .catch(function () {
        // fail-open: 検証できない時はボタンを塞がない（ops側アラートが保険）
        setDynamicCheckoutHidden(false);
      });
  }

  // ── 押下時の最終ゲート ──
  // Dawn はカート再描画でボタンごと差し替えるため、状態を持たない
  // ドキュメントレベルの委譲で捕捉する。検証は毎回 /cart.js を取り直す
  // （別タブでの変更・放置タブの時刻ずれを拾う）。見るのは締切と、描画時の
  // 検証で既に分かっている在庫問題だけ＝時刻計算のみで一瞬。在庫 API は
  // ここでは呼ばない（NOB-1766・ヘッダ参照）。
  var gateInFlight = false;

  // 遷移の直前に cart-extension-sync.js の塞ぎ（整合中・操作中・部分課金）を確認する。
  // 塞がれていれば遷移せず、ゲートを解いて次の押下に任せる（その時点ではボタン自体が
  // disabled）。
  function extensionSyncGated() {
    var sync = window.TRCartExtensionSync;
    return !!(sync && typeof sync.isGated === 'function' && sync.isGated());
  }

  function goToCheckout() {
    if (extensionSyncGated()) {
      gateInFlight = false;
      return;
    }
    window.location.href = '/checkout';
  }

  function runCheckoutGate() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var issues = collectIssues(cart);
        pruneStockIssues(cart);
        if (hasIssues(issues) || stockIssues.length > 0) {
          gateInFlight = false;
          applyNotices(issues, stockIssues, cart);
          alert(allNoticeText(issues, stockIssues));
          return;
        }
        goToCheckout();
      })
      .catch(function () {
        // fail-open: 通信断でチェックアウトを止めない（ops側アラートが保険）
        goToCheckout();
      });
  }

  document.addEventListener('click', function (ev) {
    var btn = ev.target && ev.target.closest
      ? ev.target.closest('button[name="checkout"]')
      : null;
    if (!btn) return;

    ev.preventDefault();
    if (gateInFlight) return;
    gateInFlight = true;
    runCheckoutGate();
  }, true);

  // チェックアウトから戻る操作（bfcache復元）でゲートが掛かったままに
  // ならないようにリセットする。
  window.addEventListener('pageshow', function () {
    gateInFlight = false;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshNotices);
  } else {
    refreshNotices();
  }

  // 15時JST・深夜0時JSTの境界をまたぐと判定結果が変わる。開きっぱなしの
  // カートタブでは動的チェックアウトボタン（iframe）を押下時に検証できない
  // ため、境界ちょうどでまず動的ボタンを塞ぎ、時計の誤差マージンを置いて
  // から再検証する（有効なカートなら refreshNotices が復元する）。
  // 以降も境界ごとに繰り返す。
  function scheduleBoundaryRefresh() {
    var jst = nowJST();
    var secOfDay = jst.getUTCHours() * 3600 + jst.getUTCMinutes() * 60 +
      jst.getUTCSeconds();
    var cutoff = LATE_CUTOFF_HOUR_JST * 3600;
    var untilNext = secOfDay < cutoff ? cutoff - secOfDay : 24 * 3600 - secOfDay;
    setTimeout(function () {
      setDynamicCheckoutHidden(true);
      setTimeout(function () {
        refreshNotices();
        scheduleBoundaryRefresh();
      }, 5000);
    }, untilNext * 1000);
  }
  scheduleBoundaryRefresh();

  // バックグラウンドタブではタイマーが間引かれるため、タブ復帰時にも
  // 再検証する（スリープ復帰・タブ切り替えで境界をまたいだケースの保険）。
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshNotices();
  });

  // カート内容の変更（数量変更・削除）後に案内を追従させる。
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      // Dawn のセクション再描画完了を待ってから DOM を触る
      setTimeout(refreshNotices, 0);
    });
  }
})();
